import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/material';
import moment from 'moment';
import {
  AdminsFilter,
  DateRangeFilter,
  EmployeesFilter,
  FilterButton,
  Filters,
  SelectFilter,
} from 'components/filters';
import { APP, ROUTES } from 'config';
import {
  PageContentLayout,
  PageHeaderLayout,
  Link,
  ListTotal,
  SideMenu,
  Table,
  TableToolbar,
  TableWrapper,
} from 'melp-design/components';
import { logActions, logsFiltersSchema, useLogs } from 'store/logs';
import { useRouteMatch, Redirect } from 'react-router-dom';
import { useSearchParams } from 'utils/navigation';
import { useMe } from 'state/Administrators';
import { EmployeeCell } from 'components/table';
import { LogDescription } from '../atoms';

export const LogsList: FC = () => {
  const { t } = useTranslation();
  const { path } = useRouteMatch();
  const { hasViewPermission } = useMe();

  const { searchParams: filters, navigate } =
    useSearchParams(logsFiltersSchema);

  const { data: logs, isLoading } = useLogs(filters);

  if (!hasViewPermission(['employees'])) {
    return <Redirect to={ROUTES.root.notFound} />;
  }

  return (
    <Stack gap={2}>
      <PageHeaderLayout
        breadcrumbs={[
          {
            label: t('sidebar.log'),
          },
        ]}
      />

      <PageContentLayout
        sidebar={
          <SideMenu
            tabs={[
              {
                key: 'logs',
                label: t('sidebar.log'),
                url: ROUTES.logs.list,
                isActive: path === ROUTES.logs.list,
              },
            ]}
          />
        }
      >
        <TableWrapper>
          <TableToolbar>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              gap="10px"
            >
              <ListTotal total={logs?.total} />

              <Stack direction="row" alignItems="center" gap="10px">
                <Filters
                  value={filters}
                  initialValues={logsFiltersSchema.parse({})}
                  onChange={(v) => navigate({ params: { ...v, page: 1 } })}
                >
                  {({ value, initialValues, setFilter }) => (
                    <>
                      {hasViewPermission(['employees']) ? (
                        <FilterButton
                          label={t('entity_type.employee')}
                          value={value.employees}
                          initialValue={initialValues.employees}
                          onChange={(v) => setFilter('employees', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <EmployeesFilter
                              value={{ value }}
                              onApplyFilter={({ value }) => applyFilter(value)}
                              onClearFilter={clearFilter}
                              onCancel={close}
                            />
                          )}
                        </FilterButton>
                      ) : null}

                      <FilterButton
                        label={t('entity_type.admin')}
                        value={value.admins}
                        initialValue={initialValues.admins}
                        onChange={(v) => setFilter('admins', v)}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <AdminsFilter
                            value={{ value }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                          />
                        )}
                      </FilterButton>

                      <FilterButton
                        label={t('common.date')}
                        value={value.date}
                        initialValue={initialValues.date}
                        onChange={(v) => setFilter('date', v)}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <DateRangeFilter
                            value={value}
                            onApply={applyFilter}
                            onClear={clearFilter}
                            onCancel={close}
                          />
                        )}
                      </FilterButton>

                      <FilterButton
                        label={t('common.action')}
                        value={value.actions}
                        initialValue={initialValues.actions}
                        onChange={(v) => setFilter('actions', v)}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <SelectFilter
                            value={{ value }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                            multiple
                            options={logActions.map((action) => ({
                              key: action,
                              label: t(`logs.action.${action}`),
                            }))}
                          />
                        )}
                      </FilterButton>
                    </>
                  )}
                </Filters>
              </Stack>
            </Stack>
          </TableToolbar>

          <Table
            data={logs?.items ?? []}
            isLoading={isLoading}
            columns={[
              {
                id: 'date',
                header: t('common.date'),
                cell: ({ row: { original: log } }) =>
                  moment(log.createdAt).format('l LT'),
              },
              {
                id: 'owner',
                header: t('common.done_by'),
                cell: ({ row: { original: log } }) =>
                  log.owner?.role === 'employee' ? (
                    <EmployeeCell
                      employee={{
                        id: log.owner.id,
                        fullName: log.owner.name,
                      }}
                    />
                  ) : log.owner?.role === 'hrAdmin' ? (
                    <Link
                      label={log.owner.name}
                      to={`${ROUTES.administrators.details.replace(
                        ':id',
                        log.owner.id,
                      )}?tab=administratorsInformation`}
                    />
                  ) : log.owner ? (
                    log.owner.name
                  ) : (
                    '-'
                  ),
              },
              {
                id: 'action',
                header: t('common.action'),
                cell: ({ row: { original: log } }) => (
                  <LogDescription log={log} />
                ),
              },
            ]}
            pagination={
              logs && {
                count: logs.total,
                page: logs.page,
                pageSize: logs.pageSize,
                pageSizeOptions: APP.pagination.defaults.pageSize,
                onChange: (page, pageSize) => {
                  navigate({ params: { page, pageSize } });
                },
              }
            }
          />
        </TableWrapper>
      </PageContentLayout>
    </Stack>
  );
};
